import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  SelectChangeEvent,
  Checkbox,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  SaveAlt as SaveIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  FormatPaint as TemplateIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Notifications as NotificationsIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import * as invoiceService from '../../services/invoiceService';
import * as branchInfoService from '../../services/branchInfoService';
import * as printTemplateService from '../../services/printTemplateService';
import { taxService } from '../../services/taxService';
import { getAccounts } from '../../services/financeService';
import { getWarehouses } from '../../services/warehouseService';
import { getCategories } from '../../services/categoryService';
import { IInvoicePage, IInvoicePageCreate, InvoiceType, QuantityEffect, IBranchInfo } from '../../types/invoice';
import { ITax } from '../../types/tax';
import { Warehouse } from '../../types/warehouse';
import { Account } from '../../services/financeService';
import { IPrintTemplate } from '../../types/printTemplate';

// Define field type options
const fieldTypes = [
  { value: 'text', label: 'نص' },
  { value: 'number', label: 'رقم' },
  { value: 'date', label: 'تاريخ' },
  { value: 'select', label: 'قائمة منسدلة' },
  { value: 'customer', label: 'عميل' },
  { value: 'supplier', label: 'مورد' },
  { value: 'barcode', label: 'باركود' }
];

// Define invoice type options
const invoiceTypes = [
  { value: 'sales_invoice', label: 'فاتورة مبيعات' },
  { value: 'sales_return', label: 'مرتجع مبيعات' },
  { value: 'purchase_invoice', label: 'فاتورة مشتريات' },
  { value: 'purchase_return', label: 'مرتجع مشتريات' },
  { value: 'quotation', label: 'عرض سعر' },
  { value: 'beginning_inventory', label: 'بضاعة أول المدة' },
  { value: 'import_invoice_return', label: 'مرتجع فاتورة استيراد' }
];

// Define quantity effect options
const quantityEffects = [
  { value: 'increase', label: 'زيادة' },
  { value: 'decrease', label: 'نقصان' },
  { value: 'none', label: 'لا تأثير' }
];

const InvoiceSettingsPage: React.FC = () => {
  const [pages, setPages] = useState<IInvoicePage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPage, setEditingPage] = useState<IInvoicePage | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Reference data for dropdowns
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [taxes, setTaxes] = useState<ITax[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [printTemplates, setPrintTemplates] = useState<IPrintTemplate[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [zatcaCertificates, setZatcaCertificates] = useState<any[]>([]);
  const [smsProviders, setSmsProviders] = useState<any[]>([]);
  const [whatsappProviders, setWhatsappProviders] = useState<any[]>([]);
  
  const [formData, setFormData] = useState<IInvoicePageCreate>({
    name: '',
    type: 'sales_invoice' as InvoiceType,
    quantityEffect: 'none' as QuantityEffect,
    enableTax: false,
    enablePaymentMethods: false,
    enablePartialPayment: false, // تفعيل خاصية الدفع الجزئي
    enableCashAccount: false,
    enableCustomerAccount: false, // تفعيل حساب العملاء
    enableSupplierAccount: false, // تفعيل حساب الموردين
    enableExcelImport: false, // إضافة خيار استيراد الإكسل
    enableMarketing: false, // إضافة خيار تفعيل قسم التسويق
    enableLoyaltyPoints: false, // إضافة خيار تفعيل نقاط الولاء
    enableNewCustomer: false, // إضافة خيار تفعيل إضافة عملاء جدد في الفاتورة
    customerPhoneDigitsLimit: 10, // القيمة الافتراضية للأرقام السعودية
    enableServiceProducts: false, // إضافة خيار تفعيل خدمات المنتج
    enableCostUpdate: false, // إضافة خيار تفعيل التكلفة
    canAddCustomer: true,
    canAddSupplier: true,
    branchInfo: {
      branchName: '',
      companyName: '',
      commercialRegister: '',
      branchAddress: '',
      phoneNumber: '',
      taxNumber: '',
      street: '',
      buildingNumber: '',
      postalCode: '',
      city: '',
      district: '',
      countryCode: ''
    },
    printTemplates: [],
    defaultPrintTemplate: undefined,
    defaultStatus: 'draft', // إضافة الحالة الافتراضية للفاتورة
    lockDate: false, // قفل حقل التاريخ
    lockInvoiceNumber: false, // قفل حقل رقم الفاتورة
    lockStatus: false, // قفل حقل الحالة
    lockWarehouse: false, // قفل حقل المستودع
    lockDiscount: false, // قفل حقل الخصم الكلي
    lockProductDiscount: false, // قفل حقل خصم المنتج
    lockProductTax: false, // قفل حقل ضريبة المنتج
    lockPrice: false, // قفل حقل السعر
    integerQuantityOnly: false, // السماح بالأرقام الصحيحة فقط في حقل الكمية
    selectedCategories: [], // التصنيفات المختارة
    productSelectionMode: 'all', // طريقة اختيار المنتجات
    zatcaPhase2Settings: {
      enabled: false,
      autoSubmit: false,
      requireApproval: false,
      certificateId: undefined
    },
    smsNotificationSettings: {
      enabled: false,
      providerId: undefined,
      senderName: '',
      messageTemplate: ''
    },
    whatsappNotificationSettings: {
      enabled: false,
      sendInvoicePdf: false,
      pdfTemplateId: '',
      hasTemplateVariables: false,
      templateVariables: [],
      providerId: undefined,
      templateName: '',
      templateLanguage: 'ar',
      mediaUrl: ''
    },
    loyaltyOtpSettings: {
      enabled: false,
      providerId: undefined,
      senderName: '',
      messageTemplate: ''
    }
  });
  const navigate = useNavigate();
  const theme = useTheme();

  // Fetch invoice pages and reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch pages and reference data in parallel
        const [pagesData, taxesData, accountsData, warehousesData, templatesData, categoriesData, certificatesData, smsProvidersData, whatsappProvidersData] = await Promise.all([
          invoiceService.getInvoicePages(),
          taxService.getAllTaxes(),
          getAccounts(),
          getWarehouses({ status: 'active' }), // Only fetch active warehouses
          printTemplateService.getPrintTemplatesForInvoiceSettings(), // استخدام الدالة الجديدة التي تستخدم صلاحيات إعدادات الفواتير
          getCategories(), // تحميل التصنيفات
          axiosInstance.get('/zatca/certificates').then(res => res.data).catch(() => []), // تحميل الشهادات
          axiosInstance.get('/marketing/sms/providers-list').then(res => res.data.providers || []).catch((err) => { console.error('خطأ في تحميل مزودي SMS:', err); return []; }), // تحميل مزودي SMS
          axiosInstance.get('/marketing/whatsapp/providers-list').then(res => res.data.providers || []).catch((err) => { console.error('خطأ في تحميل مزودي WhatsApp:', err); return []; }) // تحميل مزودي WhatsApp
        ]);
        
        setPages(pagesData);
        setTaxes(taxesData);
        setAccounts(accountsData);
        setWarehouses(warehousesData);
        setPrintTemplates(templatesData);
        setCategories(categoriesData);
        setZatcaCertificates(certificatesData);
        setSmsProviders(smsProvidersData);
        setWhatsappProviders(whatsappProvidersData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('حدث خطأ أثناء تحميل البيانات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Debug method to print current form state
  const debugFormState = () => {
    console.log('Current form state:', JSON.stringify(formData, null, 2));
    console.log('Branch info specifically:', JSON.stringify(formData.branchInfo, null, 2));
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    
    // Check if this is a branch info field
    if (name.startsWith('branch_')) {
      const fieldName = name.replace('branch_', '') as keyof IBranchInfo;
      const value = e.target.value;
      
      // Log changes to branch info fields
      console.log(`Updating branch info field: ${fieldName} = "${value}"`);
      
      setFormData(prev => {
        // Ensure branch info object exists
        const branchInfo = prev.branchInfo || {
          branchName: '',
          companyName: '',
          commercialRegister: '',
          branchAddress: '',
          phoneNumber: '',
          taxNumber: '',
          street: '',
          buildingNumber: '',
          postalCode: '',
          city: '',
          district: '',
          countryCode: ''
        };
        
        // Create updated form data with branch info field
        const updatedData = {
          ...prev,
          branchInfo: {
            ...branchInfo,
            [fieldName]: value
          }
        };
        
        return updatedData;
      });
    } else {
      // Handle regular field types
      if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
        // Handle Switch/Checkbox components
        const checked = e.target.checked;
        // إضافة تسجيل خاص بتفعيل قسم التسويق
        if (name === 'enableMarketing') {
          console.log('تحديث حالة تفعيل قسم التسويق في formData:', checked);
        }
        setFormData(prev => ({ ...prev, [name]: checked }));
      } else {
        // Handle text inputs and select components
        const value = e.target.value;
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    }
  };

  // Handle multi-select for print templates
  const handlePrintTemplatesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    const newSelectedTemplates = typeof value === 'string' ? value.split(',') : value;
    setFormData(prev => ({ ...prev, printTemplates: newSelectedTemplates }));
    
    // If the current default template is not in the selected templates, clear it
    if (formData.defaultPrintTemplate && !newSelectedTemplates.includes(formData.defaultPrintTemplate)) {
      setFormData(prev => ({ ...prev, defaultPrintTemplate: undefined }));
    }
  };

  // Open dialog for creating a new page
  const handleOpenCreateDialog = () => {
    setEditingPage(null);
    setFormData({
      name: '',
      type: 'sales_invoice',
      quantityEffect: 'none',
      enableTax: false,
      enableCashAccount: false,
      enableCustomerAccount: false, // تفعيل حساب العملاء
      enableSupplierAccount: false, // تفعيل حساب الموردين
      enablePaymentMethods: false,
      enablePartialPayment: false, // تفعيل خاصية الدفع الجزئي
      enableExcelImport: false, // إضافة خيار استيراد الإكسل
      enableMarketing: false, // إضافة خيار تفعيل قسم التسويق
      enableLoyaltyPoints: false, // إضافة خيار تفعيل نقاط الولاء
      enableNewCustomer: false, // إضافة خيار تفعيل إضافة عملاء جدد في الفاتورة
      enableServiceProducts: false, // إضافة خيار تفعيل خدمات المنتج
      enableCostUpdate: false, // إضافة خيار تفعيل التكلفة
      canAddCustomer: true,
      canAddSupplier: true,
      branchInfo: {
        branchName: '',
        companyName: '',
        commercialRegister: '',
        branchAddress: '',
        phoneNumber: '',
        taxNumber: '',
        street: '',
        buildingNumber: '',
        postalCode: '',
        city: '',
        district: '',
        countryCode: ''
      },
      printTemplates: [],
      defaultPrintTemplate: undefined,
      defaultStatus: 'draft',
      lockDate: false,
      lockInvoiceNumber: false,
      lockStatus: false,
      lockWarehouse: false,
      lockDiscount: false,
      lockProductDiscount: false,
      lockProductTax: false,
      selectedCategories: [],
      productSelectionMode: 'all',
      zatcaPhase2Settings: {
        enabled: false,
        autoSubmit: false,
        requireApproval: false,
        certificateId: undefined
      },
      loyaltyOtpSettings: {
        enabled: false,
        providerId: undefined,
        senderName: '',
        messageTemplate: ''
      },
      autoEntrySettings: {
        enabled: false,
        entries: [{
          accountSource: 'customer_supplier',
          accountId: undefined,
          valueType: 'total_with_tax',
          accountType: 'debit'
        }]
      }
    });
    setOpenDialog(true);
  };

  // Open dialog for editing an existing page
  const handleOpenEditDialog = (page: IInvoicePage) => {
    console.log('Editing page with branch info:', page.branchInfo);
    
    setEditingPage(page);
    setActiveTab(0); // Reset to first tab
    
    // Create a properly structured form data object
    const formDataObj: IInvoicePageCreate = {
      name: page.name,
      type: page.type,
      quantityEffect: page.quantityEffect,
      enableTax: page.enableTax,
      enablePaymentMethods: page.enablePaymentMethods, // Make sure this is included
      enablePartialPayment: page.enablePartialPayment || false, // تفعيل خاصية الدفع الجزئي
      enableCashAccount: page.enableCashAccount,
      enableCustomerAccount: page.enableCustomerAccount || false, // تفعيل حساب العملاء
      enableSupplierAccount: page.enableSupplierAccount || false, // تفعيل حساب الموردين
      enableExcelImport: page.enableExcelImport || false, // إضافة خيار استيراد الإكسل
      enableMarketing: page.enableMarketing || false, // إضافة خيار تفعيل قسم التسويق
      enableLoyaltyPoints: page.enableLoyaltyPoints || false, // إضافة خيار تفعيل نقاط الولاء
      enableNewCustomer: page.enableNewCustomer || false, // إضافة خيار تفعيل إضافة عملاء جدد في الفاتورة
      customerPhoneDigitsLimit: page.customerPhoneDigitsLimit || 10, // عدد أرقام الهاتف المطلوبة
      enableServiceProducts: page.enableServiceProducts || false, // إضافة خيار تفعيل خدمات المنتج
      enableCostUpdate: page.enableCostUpdate || false, // إضافة خيار تفعيل التكلفة
      defaultPriceType: page.defaultPriceType || 'retailPrice', // إضافة نوع السعر الافتراضي
      canAddCustomer: page.canAddCustomer,
      canAddSupplier: page.canAddSupplier,
      warehouseId: page.warehouseId,
      // تم حذف taxId لاعتماد ضريبة المنتج من التصنيفات
      cashAccountId: page.cashAccountId,
      printTemplates: page.printTemplates || [],
      defaultPrintTemplate: page.defaultPrintTemplate,
      defaultStatus: page.defaultStatus || 'draft', // إضافة الحالة الافتراضية
      lockDate: page.lockDate || false, // قفل حقل التاريخ
      lockInvoiceNumber: page.lockInvoiceNumber || false, // قفل حقل رقم الفاتورة
      lockStatus: page.lockStatus || false, // قفل حقل الحالة
      lockPrice: page.lockPrice || false, // قفل حقل السعر
      integerQuantityOnly: page.integerQuantityOnly || false, // السماح بالأرقام الصحيحة فقط في حقل الكمية
      lockWarehouse: page.lockWarehouse || false, // قفل حقل المستودع
      lockDiscount: page.lockDiscount || false, // قفل حقل الخصم الكلي
      lockProductDiscount: page.lockProductDiscount || false, // قفل حقل خصم المنتج
      lockProductTax: page.lockProductTax || false, // قفل حقل ضريبة المنتج
      selectedCategories: page.selectedCategories || [], // التصنيفات المختارة
      productSelectionMode: page.productSelectionMode || 'all', // طريقة اختيار المنتجات
      fields: page.fields,
      productFields: page.productFields,
      // Ensure branch info is properly initialized with all fields
      branchInfo: {
        branchName: page.branchInfo?.branchName || '',
        companyName: page.branchInfo?.companyName || '',
        commercialRegister: page.branchInfo?.commercialRegister || '',
        branchAddress: page.branchInfo?.branchAddress || '',
        phoneNumber: page.branchInfo?.phoneNumber || '',
        taxNumber: page.branchInfo?.taxNumber || '',
        street: page.branchInfo?.street || '',
        buildingNumber: page.branchInfo?.buildingNumber || '',
        postalCode: page.branchInfo?.postalCode || '',
        city: page.branchInfo?.city || '',
        district: page.branchInfo?.district || '',
        countryCode: page.branchInfo?.countryCode || ''
      },
      // إضافة إعدادات المرحلة الثانية
      zatcaPhase2Settings: page.zatcaPhase2Settings || {
        enabled: false,
        autoSubmit: false,
        requireApproval: false,
        certificateId: undefined
      },
      smsNotificationSettings: page.smsNotificationSettings || {
        enabled: false,
        providerId: undefined,
        senderName: '',
        messageTemplate: ''
      },
      whatsappNotificationSettings: page.whatsappNotificationSettings || {
        enabled: false,
        sendInvoicePdf: false,
        pdfTemplateId: '',
        hasTemplateVariables: false,
        templateVariables: [],
        providerId: undefined,
        templateName: '',
        templateLanguage: 'ar',
        mediaUrl: ''
      },
      loyaltyOtpSettings: page.loyaltyOtpSettings || {
        enabled: false,
        providerId: undefined,
        senderName: '',
        messageTemplate: ''
      },
      autoEntrySettings: page.autoEntrySettings || {
        enabled: false,
        entries: [{
          accountSource: 'customer_supplier',
          accountId: undefined,
          valueType: 'total_with_tax',
          accountType: 'debit'
        }]
      }
    };
    
    setFormData(formDataObj);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPage(null);
    setActiveTab(0); // Reset to first tab
    setError(null);
  };

  // Create a clean object for submission that explicitly structures all fields
  const createSubmissionData = () => {
    // Log important form data for debugging
    console.log('تحضير البيانات للإرسال:', JSON.stringify(formData, null, 2));
    console.log('حالة تفعيل طرق الدفع قبل التحضير:', formData.enablePaymentMethods);
    
    // Create a properly structured branch info object
    const branchInfo: IBranchInfo = {
      branchName: formData.branchInfo?.branchName || '',
      companyName: formData.branchInfo?.companyName || '',
      commercialRegister: formData.branchInfo?.commercialRegister || '',
      branchAddress: formData.branchInfo?.branchAddress || '',
      phoneNumber: formData.branchInfo?.phoneNumber || '',
      taxNumber: formData.branchInfo?.taxNumber || '',
      street: formData.branchInfo?.street || '',
      buildingNumber: formData.branchInfo?.buildingNumber || '',
      postalCode: formData.branchInfo?.postalCode || '',
      city: formData.branchInfo?.city || '',
      district: formData.branchInfo?.district || '',
      countryCode: formData.branchInfo?.countryCode || ''
    };

    // Create the full data object with all required fields
    return {
      name: formData.name,
      type: formData.type,
      quantityEffect: formData.quantityEffect,
      enableTax: formData.enableTax, // نحتفظ بخيار تفعيل الضريبة
      enablePaymentMethods: formData.enablePaymentMethods,
      enablePartialPayment: formData.enablePartialPayment, // إضافة خيار تفعيل الدفع الجزئي
      enableCashAccount: formData.enableCashAccount,
      enableCustomerAccount: formData.enableCustomerAccount, // تفعيل حساب العملاء
      enableSupplierAccount: formData.enableSupplierAccount, // تفعيل حساب الموردين
      enableExcelImport: formData.enableExcelImport, // إضافة خيار تفعيل استيراد الإكسل
      enableMarketing: formData.enableMarketing, // إضافة خيار تفعيل قسم التسويق
      enableLoyaltyPoints: formData.enableLoyaltyPoints, // إضافة خيار تفعيل نقاط الولاء
      enableNewCustomer: formData.enableNewCustomer, // إضافة خيار تفعيل إضافة عملاء جدد في الفاتورة
      customerPhoneDigitsLimit: formData.customerPhoneDigitsLimit, // عدد أرقام الهاتف المطلوبة للعملاء
      enableServiceProducts: formData.enableServiceProducts, // إضافة خيار تفعيل خدمات المنتج
      enableCostUpdate: formData.enableCostUpdate, // إضافة خيار تفعيل التكلفة
      defaultPriceType: formData.defaultPriceType, // إضافة نوع السعر الافتراضي
      canAddCustomer: formData.canAddCustomer,
      canAddSupplier: formData.canAddSupplier,
      warehouseId: formData.warehouseId,
      // تم حذف taxId واستبداله بالاعتماد على ضريبة المنتج من التصنيفات
      cashAccountId: formData.cashAccountId,
      // Add print template fields
      printTemplates: formData.printTemplates || [],
      defaultPrintTemplate: formData.defaultPrintTemplate,
      // إضافة الحالة الافتراضية للفاتورة
      defaultStatus: formData.defaultStatus,
      // إضافة خيارات قفل الحقول
      lockDate: formData.lockDate,
      lockInvoiceNumber: formData.lockInvoiceNumber,
      lockStatus: formData.lockStatus,
      lockPrice: formData.lockPrice,
      integerQuantityOnly: formData.integerQuantityOnly,
      lockWarehouse: formData.lockWarehouse,
      lockDiscount: formData.lockDiscount,
      lockProductDiscount: formData.lockProductDiscount,
      lockProductTax: formData.lockProductTax,
      selectedCategories: formData.selectedCategories || [], // التصنيفات المختارة
      productSelectionMode: formData.productSelectionMode || 'all', // طريقة اختيار المنتجات
      fields: formData.fields || [],
      productFields: formData.productFields || [],
      // إضافة إعدادات المرحلة الثانية من فاتورة
      zatcaPhase2Settings: formData.zatcaPhase2Settings || {
        enabled: false,
        autoSubmit: false,
        requireApproval: false,
        certificateId: undefined
      },
      // إضافة إعدادات الإشعارات التلقائية
      smsNotificationSettings: formData.smsNotificationSettings || {
        enabled: false,
        providerId: undefined,
        senderName: '',
        messageTemplate: ''
      },
      whatsappNotificationSettings: formData.whatsappNotificationSettings || {
        enabled: false,
        sendInvoicePdf: false,
        pdfTemplateId: '',
        hasTemplateVariables: false,
        templateVariables: [],
        providerId: undefined,
        templateName: '',
        templateLanguage: 'ar',
        mediaUrl: ''
      },
      loyaltyOtpSettings: formData.loyaltyOtpSettings || {
        enabled: false,
        providerId: undefined,
        senderName: '',
        messageTemplate: ''
      },
      autoEntrySettings: formData.autoEntrySettings || {
        enabled: false,
        entries: [{
          accountSource: 'customer_supplier',
          accountId: undefined,
          valueType: 'total_with_tax',
          accountType: 'debit'
        }]
      },
      branchInfo: branchInfo
    };
  };

  // Submit form to create or update an invoice page
  const handleSubmit = async () => {
    try {
      // Debug current form state before submission
      console.log('Form submission - form state before:', JSON.stringify(formData, null, 2));
      console.log('حالة تفعيل طرق الدفع قبل الحفظ:', formData.enablePaymentMethods);
      console.log('حالة تفعيل قسم التسويق قبل الحفظ:', formData.enableMarketing);
      
      if (!formData.name) {
        setError('يرجى إدخال اسم للصفحة');
        return;
      }

      // Validate cash account selection when enabled
      if (formData.enableCashAccount && !formData.cashAccountId) {
        setError('عند تفعيل حساب الصندوق، يجب اختيار حساب من القائمة');
        return;
      }
// Create a properly structured data object for submission
const dataToSubmit = createSubmissionData();
console.log('البيانات المرسلة للحفظ:', dataToSubmit);
console.log('حالة تفعيل طرق الدفع في البيانات المرسلة:', dataToSubmit.enablePaymentMethods);
console.log('حالة تفعيل قسم التسويق في البيانات المرسلة:', dataToSubmit.enableMarketing);

      
      console.log('Submitting with branch info:', JSON.stringify(dataToSubmit.branchInfo, null, 2));

      // Keep branchInfo in the data for the main update
      const { branchInfo } = dataToSubmit;
      
      try {
        let updatedPage;
        
        if (editingPage) {
          console.log('Updating page with full data (including branchInfo):', dataToSubmit);
          
          // Step 1: Update the page with ALL data including branchInfo
          const mainUpdateResult = await invoiceService.updateInvoicePage(editingPage._id!, dataToSubmit);
          console.log('Page updated successfully with all data:', mainUpdateResult);
          
          // Step 2: Also update branch info separately using dedicated service to ensure it's saved
          const branchUpdateResult = await branchInfoService.updatePageBranchInfo(
            editingPage._id!,
            branchInfo
          );
          
          console.log('Branch info updated successfully:', branchUpdateResult);
          
          // Step 3: Merge the results to ensure we have complete data
          // This ensures we don't lose print template data that might not be returned by the branch info update
          updatedPage = {
            ...mainUpdateResult,
            branchInfo: branchUpdateResult.branchInfo
          };
          
          console.log('Final merged page data:', updatedPage);
          
          // Make sure we update the state with the complete updated page
          setPages(pages.map(page =>
            page._id === editingPage._id ? updatedPage : page
          ));
        } else {
          // Step 1: Create new page with all data first
          console.log('Creating new page with full data:', dataToSubmit);
          updatedPage = await invoiceService.createInvoicePage(dataToSubmit);
          console.log('Created new page, checking if branch info was saved:', updatedPage);
          
          // Step 2: If creation succeeded but branch info is missing, update it separately
          if (updatedPage && updatedPage._id && (!updatedPage.branchInfo || Object.keys(updatedPage.branchInfo).length === 0)) {
            console.log('Branch info not in response, updating separately:', branchInfo);
            updatedPage = await branchInfoService.updatePageBranchInfo(
              updatedPage._id,
              branchInfo
            );
            console.log('Page updated with branch info separately:', updatedPage);
          }
          
          setPages([...pages, updatedPage]);
        }

        // Verify branch info was saved properly
        if (updatedPage && updatedPage.branchInfo) {
          console.log('Saved branch info:', updatedPage.branchInfo);
          
          // Extra check to make sure all fields were saved
          const allFieldsPresent = [
            'branchName', 'companyName', 'commercialRegister',
            'branchAddress', 'phoneNumber', 'taxNumber'
          ].every(field => typeof updatedPage.branchInfo[field] === 'string');
          
          if (!allFieldsPresent) {
            console.warn('Some branch info fields are missing in the saved data!');
          }
        } else {
          console.warn('Branch info not found in response!');
        }

        setOpenDialog(false);
        setError(null);
      } catch (err) {
        console.error('Error during page save/update:', err);
        setError('حدث خطأ أثناء حفظ الصفحة. يرجى المحاولة مرة أخرى.');
      }
    } catch (err) {
      console.error('Error in form validation:', err);
      setError('حدث خطأ أثناء التحقق من البيانات. يرجى المحاولة مرة أخرى.');
    }
  };

  // Delete an invoice page
  const handleDeletePage = async (pageId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الصفحة؟')) {
      try {
        await invoiceService.deleteInvoicePage(pageId);
        setPages(pages.filter(page => page._id !== pageId));
      } catch (err) {
        console.error('Error deleting invoice page:', err);
        setError('حدث خطأ أثناء حذف الصفحة. يرجى المحاولة مرة أخرى.');
      }
    }
  };

  // Navigate to page with invoices of specific type
  const handleViewInvoices = (pageId: string) => {
    navigate(`/invoices/${pageId}`);
  };

  // Render page cards
  const renderPageCards = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (pages.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          لا توجد صفحات فواتير مخصصة. انقر على "إضافة صفحة جديدة" لإنشاء أول صفحة.
        </Alert>
      );
    }

    return (
      <Grid container spacing={3}>
        {pages.map(page => (
          <Grid item xs={12} md={6} lg={4} key={page._id}>
            <Card 
              elevation={3}
              sx={{
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  p: 1,
                  zIndex: 1
                }}
              >
                <Tooltip title="تعديل">
                  <IconButton size="small" onClick={() => handleOpenEditDialog(page)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="حذف">
                  <IconButton size="small" onClick={() => handleDeletePage(page._id!)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">{page.name}</Typography>
                </Box>
                {page.sequentialId && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>المعرف التسلسلي:</strong> {page.sequentialId}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>نوع الفاتورة:</strong> {invoiceTypes.find(t => t.value === page.type)?.label}
                </Typography>
                {/* Display branch info if available */}
                {page.branchInfo && page.branchInfo.branchName && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>اسم الفرع:</strong> {page.branchInfo.branchName}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>تأثير الكميات:</strong> {quantityEffects.find(q => q.value === page.quantityEffect)?.label}
                </Typography>
                <Box mt={2}>
                  <Chip
                    label={page.enableTax ? 'الضريبة مفعلة' : 'الضريبة غير مفعلة'}
                    size="small"
                    color={page.enableTax ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={page.enableCashAccount ? 'حساب الصندوق مفعل' : 'حساب الصندوق غير مفعل'}
                    size="small"
                    color={page.enableCashAccount ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={page.enablePaymentMethods ? 'طرق الدفع مفعلة' : 'طرق الدفع غير مفعلة'}
                    size="small"
                    color={page.enablePaymentMethods ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  {page.enablePartialPayment && (
                    <Chip
                      label="الدفع الجزئي مفعل"
                      size="small"
                      color="success"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}
                  <Chip
                    label={page.enableExcelImport ? 'استيراد الإكسل مفعل' : 'استيراد الإكسل غير مفعل'}
                    size="small"
                    color={page.enableExcelImport ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={page.enableMarketing ? 'قسم التسويق مفعل' : 'قسم التسويق غير مفعل'}
                    size="small"
                    color={page.enableMarketing ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Chip
                    label={page.enableNewCustomer ? 'إضافة عملاء جدد مفعلة' : 'إضافة عملاء جدد غير مفعلة'}
                    size="small"
                    color={page.enableNewCustomer ? 'success' : 'default'}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  {page.canAddCustomer && (
                    <Chip
                      label="إضافة عملاء"
                      size="small"
                      color="info"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  )}
                  {page.canAddSupplier && (
                    <Chip
                      label="إضافة موردين"
                      size="small"
                      color="info"
                      sx={{ mb: 1 }}
                    />
                  )}
                  {/* Display company name if available */}
                  {page.branchInfo && page.branchInfo.companyName && (
                    <Chip
                      label={`الشركة: ${page.branchInfo.companyName}`}
                      size="small"
                      color="info"
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                  
                  {/* Display tax number if available */}
                  {page.branchInfo && page.branchInfo.taxNumber && (
                    <Chip
                      label={`الرقم الضريبي: ${page.branchInfo.taxNumber}`}
                      size="small"
                      color="warning"
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => {
                    console.log(`[InvoiceSettingsPage] فتح الفاتورة بمعرف ${page._id} والمعرف التسلسلي ${page.sequentialId}`);
                    handleViewInvoices(page._id!);
                  }}
                >
                  عرض الفواتير
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            إعدادات الفواتير
          </Typography>
          <Typography variant="body1" color="text.secondary">
            قم بإنشاء وإدارة صفحات الفواتير المخصصة لمختلف أنواع المعاملات
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<TemplateIcon />}
            onClick={() => navigate('/invoices/print-templates')}
            sx={{ mr: 2 }}
          >
            قوالب الطباعة
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            إضافة صفحة جديدة
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {renderPageCards()}

      {/* Dialog for creating/editing invoice pages */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPage ? 'تعديل صفحة الفاتورة' : 'إنشاء صفحة فاتورة جديدة'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2, bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 1 }}
          >
            <Tab label="الإعدادات الأساسية" icon={<SettingsIcon />} iconPosition="start" />
            <Tab label="الإشعارات" icon={<NotificationsIcon />} iconPosition="start" />
            <Tab label="القيد التلقائي" icon={<AccountBalanceIcon />} iconPosition="start" />
          </Tabs>
          <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم الصفحة"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>نوع الفاتورة</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="نوع الفاتورة"
                >
                  {invoiceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>الحالة الافتراضية للفاتورة</InputLabel>
                <Select
                  name="defaultStatus"
                  value={formData.defaultStatus || 'draft'}
                  onChange={handleInputChange}
                  label="الحالة الافتراضية للفاتورة"
                >
                  <MenuItem value="draft">مسودة</MenuItem>
                  <MenuItem value="completed">مكتملة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>تأثير الكميات</InputLabel>
                <Select
                  name="quantityEffect"
                  value={formData.quantityEffect}
                  onChange={handleInputChange}
                  label="تأثير الكميات"
                >
                  {quantityEffects.map(effect => (
                    <MenuItem key={effect.value} value={effect.value}>
                      {effect.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Warehouse Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>المستودع الافتراضي</InputLabel>
                <Select
                  name="warehouseId"
                  value={formData.warehouseId || ''}
                  onChange={handleInputChange}
                  label="المستودع الافتراضي"
                >
                  <MenuItem value="">لا يوجد</MenuItem>
                  {warehouses.map(warehouse => (
                    <MenuItem key={warehouse._id} value={warehouse._id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Price Type Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                إعدادات الأسعار
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>نوع السعر الافتراضي</InputLabel>
                <Select
                  name="defaultPriceType"
                  value={formData.defaultPriceType || 'retailPrice'}
                  onChange={handleInputChange}
                  label="نوع السعر الافتراضي"
                >
                  <MenuItem value="retailPrice">سعر التجزئة</MenuItem>
                  <MenuItem value="wholesalePrice">سعر الجملة</MenuItem>
                  <MenuItem value="customPrice1">سعر مخصص 1</MenuItem>
                  <MenuItem value="customPrice2">سعر مخصص 2</MenuItem>
                  <MenuItem value="lastCost">آخر تكلفة</MenuItem>
                  <MenuItem value="averageCost">متوسط التكلفة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Product Selection Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                اختيار المنتجات للفاتورة
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                حدد التصنيفات التي ستكون منتجاتها متاحة في هذه الفاتورة. إذا لم تختر أي تصنيف، ستكون جميع المنتجات متاحة.
              </Typography>
            </Grid>
            
            {/* Category Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>التصنيفات المختارة</InputLabel>
                <Select
                  multiple
                  name="selectedCategories"
                  value={formData.selectedCategories || []}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      selectedCategories: value,
                      // تحديث نمط اختيار المنتجات تلقائياً
                      productSelectionMode: value.length > 0 ? 'category' : 'all'
                    }));
                  }}
                  label="التصنيفات المختارة"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((categoryId) => {
                        const category = categories.find(c => c._id === categoryId);
                        return (
                          <Chip key={categoryId} label={category?.name || categoryId} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  <MenuItem value="">
                    <em>جميع التصنيفات (افتراضي)</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      <Checkbox checked={(formData.selectedCategories || []).indexOf(category._id) > -1} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Financial Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                الإعدادات المالية
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableTax || false}
                    onChange={handleInputChange}
                    name="enableTax"
                    color="primary"
                  />
                }
                label="تفعيل الضريبة"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCashAccount || false}
                    onChange={handleInputChange}
                    name="enableCashAccount"
                    color="primary"
                  />
                }
                label="تفعيل حساب الصندوق"
              />
            </Grid>
            
            {/* Cash account selection - shown directly under enableCashAccount */}
            {formData.enableCashAccount && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>حساب الصندوق</InputLabel>
                  <Select
                    name="cashAccountId"
                    value={formData.cashAccountId || ''}
                    onChange={handleInputChange}
                    onBlur={debugFormState}
                    label="حساب الصندوق"
                    required
                    error={formData.enableCashAccount && !formData.cashAccountId}
                  >
                    <MenuItem value="">اختر حساب الصندوق</MenuItem>
                    {accounts.map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCustomerAccount || false}
                    onChange={handleInputChange}
                    name="enableCustomerAccount"
                    color="primary"
                  />
                }
                label="تفعيل حساب العملاء"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableSupplierAccount || false}
                    onChange={handleInputChange}
                    name="enableSupplierAccount"
                    color="primary"
                  />
                }
                label="تفعيل حساب الموردين"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enablePaymentMethods || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل طرق الدفع:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enablePaymentMethods"
                    color="primary"
                  />
                }
                label="تفعيل طرق الدفع"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enablePartialPayment || false}
                    onChange={handleInputChange}
                    name="enablePartialPayment"
                    color="primary"
                  />
                }
                label="تفعيل الدفع الجزئي"
              />
            </Grid>
            
            {/* ZATCA Phase 2 Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                إعدادات المرحلة الثانية من الفاتورة الإلكترونية (ZATCA Phase 2)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.zatcaPhase2Settings?.enabled || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        zatcaPhase2Settings: {
                          ...prev.zatcaPhase2Settings,
                          enabled: isChecked,
                          autoSubmit: prev.zatcaPhase2Settings?.autoSubmit || false,
                          requireApproval: prev.zatcaPhase2Settings?.requireApproval || false,
                          certificateId: prev.zatcaPhase2Settings?.certificateId
                        }
                      }));
                    }}
                    name="zatcaPhase2Enabled"
                    color="primary"
                  />
                }
                label="تفعيل المرحلة الثانية"
              />
            </Grid>
            
            {formData.zatcaPhase2Settings?.enabled && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>اختر الشهادة</InputLabel>
                    <Select
                      name="zatcaPhase2CertificateId"
                      value={formData.zatcaPhase2Settings?.certificateId || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          zatcaPhase2Settings: {
                            ...prev.zatcaPhase2Settings,
                            enabled: prev.zatcaPhase2Settings?.enabled || false,
                            autoSubmit: prev.zatcaPhase2Settings?.autoSubmit || false,
                            requireApproval: prev.zatcaPhase2Settings?.requireApproval || false,
                            certificateId: e.target.value
                          }
                        }));
                      }}
                      label="اختر الشهادة"
                      required
                    >
                      <MenuItem value="">اختر شهادة</MenuItem>
                      {zatcaCertificates.map(cert => (
                        <MenuItem key={cert._id} value={cert._id}>
                          {cert.name} ({cert.environment === 'sandbox' ? 'تجريبي' : 'إنتاج'})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.zatcaPhase2Settings?.autoSubmit || false}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            zatcaPhase2Settings: {
                              ...prev.zatcaPhase2Settings,
                              enabled: prev.zatcaPhase2Settings?.enabled || false,
                              autoSubmit: e.target.checked,
                              requireApproval: prev.zatcaPhase2Settings?.requireApproval || false,
                              certificateId: prev.zatcaPhase2Settings?.certificateId
                            }
                          }));
                        }}
                        name="zatcaPhase2AutoSubmit"
                        color="primary"
                      />
                    }
                    label="إرسال تلقائي عند حفظ الفاتورة"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.zatcaPhase2Settings?.requireApproval || false}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            zatcaPhase2Settings: {
                              ...prev.zatcaPhase2Settings,
                              enabled: prev.zatcaPhase2Settings?.enabled || false,
                              autoSubmit: prev.zatcaPhase2Settings?.autoSubmit || false,
                              requireApproval: e.target.checked,
                              certificateId: prev.zatcaPhase2Settings?.certificateId
                            }
                          }));
                        }}
                        name="zatcaPhase2RequireApproval"
                        color="primary"
                      />
                    }
                    label="يتطلب موافقة قبل الإرسال"
                  />
                </Grid>
              </>
            )}
            
            {/* Customer & Supplier Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                إعدادات العملاء والموردين
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.canAddCustomer}
                    onChange={handleInputChange}
                    name="canAddCustomer"
                    color="primary"
                  />
                }
                label="السماح بإضافة عملاء"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.canAddSupplier}
                    onChange={handleInputChange}
                    name="canAddSupplier"
                    color="primary"
                  />
                }
                label="السماح بإضافة موردين"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableNewCustomer || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل إضافة عملاء جدد:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enableNewCustomer"
                    color="primary"
                  />
                }
                label="تفعيل إضافة عملاء جدد في الفاتورة"
              />
            </Grid>
            
            {/* حقل عدد أرقام الهاتف - يظهر فقط عند تفعيل إضافة عملاء جدد */}
            {formData.enableNewCustomer && (
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <TextField
                    name="customerPhoneDigitsLimit"
                    label="عدد أرقام الهاتف المطلوبة للعملاء"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.customerPhoneDigitsLimit || 10}
                    onChange={handleInputChange}
                    inputProps={{ min: 7, max: 15 }}
                    helperText="حدد عدد الأرقام المطلوبة في رقم هاتف العميل (7-15 رقم). مثال: 10 للأرقام السعودية"
                  />
                </Box>
              </Grid>
            )}
            
            {/* Additional Features Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                الميزات الإضافية
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableMarketing || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل قسم التسويق:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enableMarketing"
                    color="primary"
                  />
                }
                label="تفعيل قسم التسويق"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableServiceProducts || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل خدمات المنتج:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enableServiceProducts"
                    color="primary"
                  />
                }
                label="تفعيل خدمات المنتج في الفاتورة"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableLoyaltyPoints || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل نقاط الولاء:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enableLoyaltyPoints"
                    color="primary"
                  />
                }
                label="تفعيل نقاط الولاء في الفاتورة"
              />
            </Grid>
            
            {/* إعدادات OTP لنقاط الولاء - تظهر فقط عند تفعيل نقاط الولاء */}
            {formData.enableLoyaltyPoints && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    إعدادات تأكيد استبدال النقاط عبر OTP
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.loyaltyOtpSettings?.enabled || false}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            loyaltyOtpSettings: {
                              ...prev.loyaltyOtpSettings,
                              enabled: e.target.checked,
                              providerId: prev.loyaltyOtpSettings?.providerId,
                              senderName: prev.loyaltyOtpSettings?.senderName || '',
                              messageTemplate: prev.loyaltyOtpSettings?.messageTemplate || ''
                            }
                          }));
                        }}
                        name="loyaltyOtpEnabled"
                        color="primary"
                      />
                    }
                    label="تفعيل تأكيد الاستبدال عبر OTP"
                  />
                </Grid>
                
                {formData.loyaltyOtpSettings?.enabled && (
                  <>
                    {/* مزود خدمة SMS */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>مزود خدمة SMS</InputLabel>
                        <Select
                          value={formData.loyaltyOtpSettings?.providerId || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              loyaltyOtpSettings: {
                                ...prev.loyaltyOtpSettings!,
                                providerId: e.target.value
                              }
                            }));
                          }}
                          label="مزود خدمة SMS"
                        >
                          <MenuItem value="">اختر المزود</MenuItem>
                          {smsProviders.map(provider => (
                            <MenuItem key={provider._id} value={provider._id}>
                              {provider.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* اسم المرسل */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="اسم المرسل"
                        value={formData.loyaltyOtpSettings?.senderName || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            loyaltyOtpSettings: {
                              ...prev.loyaltyOtpSettings!,
                              senderName: e.target.value
                            }
                          }));
                        }}
                        margin="normal"
                        inputProps={{ maxLength: 11 }}
                        helperText="حد أقصى 11 حرف"
                      />
                    </Grid>
                    
                    {/* نص الرسالة */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="نص رسالة OTP"
                        value={formData.loyaltyOtpSettings?.messageTemplate || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            loyaltyOtpSettings: {
                              ...prev.loyaltyOtpSettings!,
                              messageTemplate: e.target.value
                            }
                          }));
                        }}
                        margin="normal"
                        multiline
                        rows={2}
                        helperText="استخدم المتغير {otp} لإدراج رمز التحقق. مثال: رمز التحقق الخاص بك هو: {otp}"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableExcelImport || false}
                    onChange={handleInputChange}
                    name="enableExcelImport"
                    color="primary"
                  />
                }
                label="تفعيل استيراد المنتجات من الإكسل"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enableCostUpdate || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      console.log('تغيير حالة تفعيل التكلفة:', isChecked);
                      handleInputChange(e);
                    }}
                    name="enableCostUpdate"
                    color="primary"
                  />
                }
                label="تفعيل تحديث التكلفة من الفواتير"
              />
            </Grid>
            
            {/* تم حذف اختيار الضريبة الافتراضية - سيتم الاعتماد على الضريبة التي ينتمي لها متغير المنتج بالتصنيفات */}
            
            
            {/* Field Lock Options Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                خيارات قفل الحقول
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                يمكنك قفل الحقول التالية لمنع تغييرها في الفاتورة نفسها
              </Typography>
            </Grid>
            
            {/* Lock Date Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockDate || false}
                    onChange={handleInputChange}
                    name="lockDate"
                    color="primary"
                  />
                }
                label="قفل حقل التاريخ"
              />
            </Grid>
            
            {/* Lock Invoice Number Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockInvoiceNumber || false}
                    onChange={handleInputChange}
                    name="lockInvoiceNumber"
                    color="primary"
                  />
                }
                label="قفل حقل رقم الفاتورة"
              />
            </Grid>
            
            {/* Lock Status Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockStatus || false}
                    onChange={handleInputChange}
                    name="lockStatus"
                    color="primary"
                  />
                }
                label="قفل حقل الحالة"
              />
            </Grid>
            
            {/* Lock Warehouse Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockWarehouse || false}
                    onChange={handleInputChange}
                    name="lockWarehouse"
                    color="primary"
                  />
                }
                label="قفل حقل المستودع"
              />
            </Grid>
            
            {/* Lock Discount Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockDiscount || false}
                    onChange={handleInputChange}
                    name="lockDiscount"
                    color="primary"
                  />
                }
                label="قفل حقل الخصم الكلي"
              />
            </Grid>
            
            {/* Lock Product Discount Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockProductDiscount || false}
                    onChange={handleInputChange}
                    name="lockProductDiscount"
                    color="primary"
                  />
                }
                label="قفل حقل خصم المنتج"
              />
            </Grid>
            
            {/* Lock Product Tax Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockProductTax || false}
                    onChange={handleInputChange}
                    name="lockProductTax"
                    color="primary"
                  />
                }
                label="قفل حقل ضريبة المنتج"
              />
            </Grid>
            
            {/* Lock Price Field */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.lockPrice || false}
                    onChange={handleInputChange}
                    name="lockPrice"
                    color="primary"
                  />
                }
                label="قفل حقل السعر"
              />
            </Grid>
            
            {/* Integer Quantity Only */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.integerQuantityOnly || false}
                    onChange={handleInputChange}
                    name="integerQuantityOnly"
                    color="primary"
                  />
                }
                label="كمية صحيحة فقط (بدون كسور عشرية)"
              />
            </Grid>
            
            {/* Branch Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                معلومات الفرع
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            {/* Branch Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم الفرع"
                name="branch_branchName"
                value={formData.branchInfo?.branchName || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Company Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="اسم الشركة"
                name="branch_companyName"
                value={formData.branchInfo?.companyName || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Commercial Register */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="السجل التجاري"
                name="branch_commercialRegister"
                value={formData.branchInfo?.commercialRegister || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Branch Address */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="عنوان الفرع"
                name="branch_branchAddress"
                value={formData.branchInfo?.branchAddress || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رقم الهاتف"
                name="branch_phoneNumber"
                value={formData.branchInfo?.phoneNumber || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Print Templates Section Header */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                قوالب الطباعة
              </Typography>
            </Grid>
            
            {/* Print Templates Selection */}
            <Grid item xs={12} md={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="print-templates-label">قوالب الطباعة المتاحة</InputLabel>
                <Select
                  labelId="print-templates-label"
                  multiple
                  name="printTemplates"
                  value={formData.printTemplates || []}
                  onChange={handlePrintTemplatesChange}
                  renderValue={(selected) => {
                    const selectedNames = selected.map(id =>
                      printTemplates.find(t => t._id === id)?.name || id
                    ).join(', ');
                    return selectedNames;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    }
                  }}
                >
                  {printTemplates
                    .filter(template => template.invoiceType === formData.type || template.invoiceType === 'all')
                    .map(template => (
                      <MenuItem key={template._id} value={template._id}>
                        <Checkbox checked={(formData.printTemplates || []).indexOf(template._id || '') > -1} />
                        {template.name} ({template.displayLanguage === 'ar' ? 'عربي' : 'إنجليزي'})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Default Print Template */}
            <Grid item xs={12} md={12}>
              <FormControl fullWidth margin="normal" disabled={(formData.printTemplates || []).length === 0}>
                <InputLabel>قالب الطباعة الافتراضي</InputLabel>
                <Select
                  name="defaultPrintTemplate"
                  value={formData.defaultPrintTemplate || ''}
                  onChange={handleInputChange}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>لا يوجد قالب افتراضي</em>
                  </MenuItem>
                  {(formData.printTemplates || []).map(templateId => {
                    const template = printTemplates.find(t => t._id === templateId);
                    return template ? (
                      <MenuItem key={template._id} value={template._id}>
                        {template.name} ({template.displayLanguage === 'ar' ? 'عربي' : 'إنجليزي'})
                      </MenuItem>
                    ) : null;
                  })}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Tax Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="الرقم الضريبي"
                name="branch_taxNumber"
                value={formData.branchInfo?.taxNumber || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Street */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="الشارع"
                name="branch_street"
                value={formData.branchInfo?.street || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Building Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رقم المبنى"
                name="branch_buildingNumber"
                value={formData.branchInfo?.buildingNumber || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Postal Code */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="الرمز البريدي"
                name="branch_postalCode"
                value={formData.branchInfo?.postalCode || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* City */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="المدينة"
                name="branch_city"
                value={formData.branchInfo?.city || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* District */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="الحي"
                name="branch_district"
                value={formData.branchInfo?.district || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
              />
            </Grid>
            
            {/* Country Code */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رمز البلد"
                name="branch_countryCode"
                value={formData.branchInfo?.countryCode || ''}
                onChange={handleInputChange}
                onBlur={debugFormState}
                margin="normal"
                placeholder="مثال: SA"
                helperText="رمز البلد المكون من حرفين (مثل: SA للسعودية)"
              />
            </Grid>
          </Grid>
          )}
          
          {activeTab === 1 && (
          <Grid container spacing={2}>
            {/* SMS Notification Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                <SmsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                إعدادات إرسال SMS التلقائي
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                عند تفعيل هذه الخاصية، سيتم إرسال رسالة SMS تلقائياً للعميل عند إصدار الفاتورة
              </Typography>
            </Grid>

            {/* Enable SMS Notifications */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.smsNotificationSettings?.enabled || false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        smsNotificationSettings: {
                          ...prev.smsNotificationSettings,
                          enabled: e.target.checked,
                          providerId: prev.smsNotificationSettings?.providerId,
                          senderName: prev.smsNotificationSettings?.senderName || '',
                          messageTemplate: prev.smsNotificationSettings?.messageTemplate || ''
                        }
                      }));
                    }}
                    name="smsNotificationEnabled"
                    color="primary"
                  />
                }
                label="تفعيل إرسال SMS تلقائياً عند إصدار الفاتورة"
              />
            </Grid>

            {formData.smsNotificationSettings?.enabled && (
              <>
                {/* SMS Provider Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>مزود خدمة SMS</InputLabel>
                    <Select
                      value={formData.smsNotificationSettings?.providerId || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          smsNotificationSettings: {
                            ...prev.smsNotificationSettings!,
                            providerId: e.target.value
                          }
                        }));
                      }}
                      label="مزود خدمة SMS"
                    >
                      <MenuItem value="">اختر المزود</MenuItem>
                      {smsProviders.map(provider => (
                        <MenuItem key={provider._id} value={provider._id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* SMS Sender Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="اسم المرسل"
                    value={formData.smsNotificationSettings?.senderName || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        smsNotificationSettings: {
                          ...prev.smsNotificationSettings!,
                          senderName: e.target.value
                        }
                      }));
                    }}
                    margin="normal"
                    inputProps={{ maxLength: 11 }}
                    helperText="حد أقصى 11 حرف"
                  />
                </Grid>

                {/* SMS Message Template */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="نص الرسالة"
                    value={formData.smsNotificationSettings?.messageTemplate || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        smsNotificationSettings: {
                          ...prev.smsNotificationSettings!,
                          messageTemplate: e.target.value
                        }
                      }));
                    }}
                    margin="normal"
                    multiline
                    rows={3}
                    helperText="يمكنك استخدام المتغيرات: {invoiceNumber}, {total}, {customerName}, {date}"
                  />
                </Grid>
              </>
            )}

            {/* WhatsApp Notification Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                <WhatsAppIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                إعدادات إرسال WhatsApp التلقائي
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                عند تفعيل هذه الخاصية، سيتم إرسال رسالة WhatsApp تلقائياً للعميل عند إصدار الفاتورة
              </Typography>
            </Grid>

            {/* Enable WhatsApp Notifications */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.whatsappNotificationSettings?.enabled || false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        whatsappNotificationSettings: {
                          ...prev.whatsappNotificationSettings,
                          enabled: e.target.checked,
                          providerId: prev.whatsappNotificationSettings?.providerId,
                          templateName: prev.whatsappNotificationSettings?.templateName || '',
                          templateLanguage: prev.whatsappNotificationSettings?.templateLanguage || 'ar',
                          mediaUrl: prev.whatsappNotificationSettings?.mediaUrl || '',
                          sendInvoicePdf: prev.whatsappNotificationSettings?.sendInvoicePdf || false,
                          pdfTemplateId: prev.whatsappNotificationSettings?.pdfTemplateId || '',
                          hasTemplateVariables: prev.whatsappNotificationSettings?.hasTemplateVariables || false,
                          templateVariables: prev.whatsappNotificationSettings?.templateVariables || []
                        }
                      }));
                    }}
                    name="whatsappNotificationEnabled"
                    color="primary"
                  />
                }
                label="تفعيل إرسال WhatsApp تلقائياً عند إصدار الفاتورة"
              />
            </Grid>

            {formData.whatsappNotificationSettings?.enabled && (
              <>
                {/* WhatsApp Provider Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>مزود خدمة WhatsApp</InputLabel>
                    <Select
                      value={formData.whatsappNotificationSettings?.providerId || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          whatsappNotificationSettings: {
                            ...prev.whatsappNotificationSettings!,
                            providerId: e.target.value
                          }
                        }));
                      }}
                      label="مزود خدمة WhatsApp"
                    >
                      <MenuItem value="">اختر المزود</MenuItem>
                      {whatsappProviders.map(provider => (
                        <MenuItem key={provider._id} value={provider._id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* WhatsApp Template Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="اسم القالب"
                    value={formData.whatsappNotificationSettings?.templateName || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        whatsappNotificationSettings: {
                          ...prev.whatsappNotificationSettings!,
                          templateName: e.target.value
                        }
                      }));
                    }}
                    margin="normal"
                    helperText="اسم القالب المعتمد من WhatsApp"
                  />
                </Grid>

                {/* WhatsApp Template Language */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>لغة القالب</InputLabel>
                    <Select
                      value={formData.whatsappNotificationSettings?.templateLanguage || 'ar'}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          whatsappNotificationSettings: {
                            ...prev.whatsappNotificationSettings!,
                            templateLanguage: e.target.value as 'ar' | 'en'
                          }
                        }));
                      }}
                      label="لغة القالب"
                    >
                      <MenuItem value="ar">عربي</MenuItem>
                      <MenuItem value="en">إنجليزي</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* WhatsApp Media URL */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="رابط الملف المرفق (اختياري)"
                    value={formData.whatsappNotificationSettings?.mediaUrl || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        whatsappNotificationSettings: {
                          ...prev.whatsappNotificationSettings!,
                          mediaUrl: e.target.value
                        }
                      }));
                    }}
                    margin="normal"
                    helperText="رابط صورة أو ملف PDF (اختياري)"
                  />
                </Grid>

                {/* Send Invoice PDF */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.whatsappNotificationSettings?.sendInvoicePdf || false}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            whatsappNotificationSettings: {
                              ...prev.whatsappNotificationSettings!,
                              sendInvoicePdf: e.target.checked,
                              pdfTemplateId: e.target.checked ? (prev.whatsappNotificationSettings?.pdfTemplateId || '') : ''
                            }
                          }));
                        }}
                      />
                    }
                    label="إرسال الفاتورة PDF تلقائياً"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                    سيتم إنشاء PDF للفاتورة وإرساله مع الرسالة (يتطلب أن يكون السيرفر متاح على الإنترنت)
                  </Typography>
                </Grid>

                {/* PDF Template Selection */}
                {formData.whatsappNotificationSettings?.sendInvoicePdf && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>قالب الطباعة (اختياري)</InputLabel>
                      <Select
                        value={formData.whatsappNotificationSettings?.pdfTemplateId || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            whatsappNotificationSettings: {
                              ...prev.whatsappNotificationSettings!,
                              pdfTemplateId: e.target.value
                            }
                          }));
                        }}
                        label="قالب الطباعة (اختياري)"
                      >
                        <MenuItem value="">
                          <em>القالب الافتراضي</em>
                        </MenuItem>
                        {printTemplates
                          .filter(template => template.invoiceType === formData.type || template.invoiceType === 'all')
                          .map((template) => (
                            <MenuItem key={template._id} value={template._id}>
                              {template.name} ({template.displayLanguage === 'ar' ? 'عربي' : 'إنجليزي'})
                            </MenuItem>
                          ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        إذا لم تحدد قالب، سيتم استخدام القالب الافتراضي للفاتورة
                      </Typography>
                    </FormControl>
                  </Grid>
                )}

                {/* Has Template Variables */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.whatsappNotificationSettings?.hasTemplateVariables || false}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            whatsappNotificationSettings: {
                              ...prev.whatsappNotificationSettings!,
                              hasTemplateVariables: e.target.checked,
                              templateVariables: e.target.checked ? (prev.whatsappNotificationSettings?.templateVariables || []) : []
                            }
                          }));
                        }}
                      />
                    }
                    label="القالب يحتوي على متغيرات"
                  />
                </Grid>

                {/* Template Variables Selection */}
                {formData.whatsappNotificationSettings?.hasTemplateVariables && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      اختر المتغيرات المطلوبة (حسب ترتيب القالب):
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                      {[
                        { value: 'customerName', label: 'اسم العميل' },
                        { value: 'invoiceNumber', label: 'رقم الفاتورة' },
                        { value: 'total', label: 'المبلغ الإجمالي' },
                        { value: 'date', label: 'تاريخ الفاتورة' }
                      ].map((variable) => (
                        <FormControlLabel
                          key={variable.value}
                          control={
                            <Switch
                              checked={formData.whatsappNotificationSettings?.templateVariables?.includes(variable.value) || false}
                              onChange={(e) => {
                                setFormData(prev => {
                                  const currentVars = prev.whatsappNotificationSettings?.templateVariables || [];
                                  const newVars = e.target.checked
                                    ? [...currentVars, variable.value]
                                    : currentVars.filter(v => v !== variable.value);
                                  
                                  return {
                                    ...prev,
                                    whatsappNotificationSettings: {
                                      ...prev.whatsappNotificationSettings!,
                                      templateVariables: newVars
                                    }
                                  };
                                });
                              }}
                            />
                          }
                          label={variable.label}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      ملاحظة: سيتم إرسال المتغيرات حسب الترتيب الذي اخترته في القالب ({'{'}1{'}'}, {'{'}2{'}'}, {'{'}3{'}'}, {'{'}4{'}'})
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>
          )}

          {activeTab === 2 && (
          <Grid container spacing={2}>
            {/* Auto Entry Settings Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                <AccountBalanceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                إعدادات القيد التلقائي
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                عند تفعيل هذه الخاصية، سيتم إنشاء قيد محاسبي تلقائياً بعد حفظ الفاتورة
              </Typography>
            </Grid>

            {/* Enable Auto Entry */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoEntrySettings?.enabled || false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        autoEntrySettings: {
                          enabled: e.target.checked,
                          entries: prev.autoEntrySettings?.entries || [{
                            accountSource: 'customer_supplier',
                            valueType: 'total_with_tax',
                            accountType: 'debit'
                          }]
                        }
                      }));
                    }}
                    name="autoEntryEnabled"
                    color="primary"
                  />
                }
                label="تفعيل القيد التلقائي"
              />
            </Grid>

            {formData.autoEntrySettings?.enabled && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      أسطر القيد التلقائي
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          autoEntrySettings: {
                            ...prev.autoEntrySettings!,
                            entries: [
                              ...(prev.autoEntrySettings?.entries || []),
                              {
                                accountSource: 'customer_supplier',
                                valueType: 'total_with_tax',
                                accountType: 'debit'
                              }
                            ]
                          }
                        }));
                      }}
                    >
                      إضافة سطر جديد
                    </Button>
                  </Box>
                </Grid>

                {formData.autoEntrySettings?.entries?.map((entry, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12}>
                      <Paper elevation={2} sx={{ p: 2, mb: 2, position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" color="primary">
                            السطر {index + 1}
                          </Typography>
                          {(formData.autoEntrySettings?.entries?.length || 0) > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  autoEntrySettings: {
                                    ...prev.autoEntrySettings!,
                                    entries: prev.autoEntrySettings!.entries.filter((_, i) => i !== index)
                                  }
                                }));
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>

                        <Grid container spacing={2}>
                          {/* Account Source Selection */}
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>مصدر الحساب</InputLabel>
                              <Select
                                value={entry.accountSource}
                                onChange={(e) => {
                                  setFormData(prev => {
                                    const newEntries = [...(prev.autoEntrySettings?.entries || [])];
                                    newEntries[index] = {
                                      ...newEntries[index],
                                      accountSource: e.target.value as 'customer_supplier' | 'other',
                                      accountId: e.target.value === 'customer_supplier' ? undefined : newEntries[index].accountId
                                    };
                                    return {
                                      ...prev,
                                      autoEntrySettings: {
                                        ...prev.autoEntrySettings!,
                                        entries: newEntries
                                      }
                                    };
                                  });
                                }}
                                label="مصدر الحساب"
                              >
                                <MenuItem value="customer_supplier">حساب العميل/المورد</MenuItem>
                                <MenuItem value="other">حساب آخر</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Account Selection (if "other" is selected) */}
                          {entry.accountSource === 'other' && (
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>اختر الحساب</InputLabel>
                                <Select
                                  value={entry.accountId || ''}
                                  onChange={(e) => {
                                    setFormData(prev => {
                                      const newEntries = [...(prev.autoEntrySettings?.entries || [])];
                                      newEntries[index] = {
                                        ...newEntries[index],
                                        accountId: e.target.value
                                      };
                                      return {
                                        ...prev,
                                        autoEntrySettings: {
                                          ...prev.autoEntrySettings!,
                                          entries: newEntries
                                        }
                                      };
                                    });
                                  }}
                                  label="اختر الحساب"
                                >
                                  <MenuItem value="">اختر الحساب</MenuItem>
                                  {accounts.map(account => (
                                    <MenuItem key={account.id} value={account.id}>
                                      {account.accountNumber} - {account.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}

                          {/* Value Type Selection */}
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>نوع القيمة</InputLabel>
                              <Select
                                value={entry.valueType}
                                onChange={(e) => {
                                  setFormData(prev => {
                                    const newEntries = [...(prev.autoEntrySettings?.entries || [])];
                                    newEntries[index] = {
                                      ...newEntries[index],
                                      valueType: e.target.value as any
                                    };
                                    return {
                                      ...prev,
                                      autoEntrySettings: {
                                        ...prev.autoEntrySettings!,
                                        entries: newEntries
                                      }
                                    };
                                  });
                                }}
                                label="نوع القيمة"
                              >
                                <MenuItem value="total_with_tax">إجمالي الفاتورة شامل الضريبة</MenuItem>
                                <MenuItem value="subtotal_before_discount">المجموع الفرعي قبل الخصم</MenuItem>
                                <MenuItem value="subtotal_after_discount">المجموع الفرعي بعد الخصم</MenuItem>
                                <MenuItem value="tax_amount">الضريبة</MenuItem>
                                <MenuItem value="total_discount">الخصم الكلي</MenuItem>
                                <MenuItem value="total_discount_before_tax">الخصم الكلي قبل الضريبة</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Account Type Selection */}
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>نوع الحساب</InputLabel>
                              <Select
                                value={entry.accountType}
                                onChange={(e) => {
                                  setFormData(prev => {
                                    const newEntries = [...(prev.autoEntrySettings?.entries || [])];
                                    newEntries[index] = {
                                      ...newEntries[index],
                                      accountType: e.target.value as 'debit' | 'credit'
                                    };
                                    return {
                                      ...prev,
                                      autoEntrySettings: {
                                        ...prev.autoEntrySettings!,
                                        entries: newEntries
                                      }
                                    };
                                  });
                                }}
                                label="نوع الحساب"
                              >
                                <MenuItem value="debit">مدين</MenuItem>
                                <MenuItem value="credit">دائن</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </React.Fragment>
                ))}

                {/* Info Alert */}
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>ملاحظة:</strong> سيتم إنشاء القيد التلقائي بعد حفظ الفاتورة مباشرة. يمكنك مراجعة القيود المنشأة من صفحة "قيود الفواتير".
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
          )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit" startIcon={<CancelIcon />}>
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            حفظ البيانات الجديدة
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceSettingsPage;